/**
 * Contractor Mobile Dashboard - Field Engineer Home Screen
 * 
 * DESIGN PRINCIPLE: Uber-driver-app-style UX
 * - Extremely fast
 * - Low friction
 * - Touch-friendly with large targets
 * - Operational & simple
 * - Status pills, bottom sticky actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================
// TOKEN MANAGEMENT
// ============================================================
const getToken = () => {
  // Try multiple token sources
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  if (urlToken) {
    localStorage.setItem('contractor_mobile_token', urlToken);
    return urlToken;
  }
  return localStorage.getItem('contractor_mobile_token') || 
         localStorage.getItem('contractor_portal_token');
};

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============================================================
// STATUS UTILITIES
// ============================================================
const getStatusConfig = (status) => {
  const statusMap = {
    'Assigned': { label: 'Assigned', bg: 'bg-blue-500', text: 'text-white', priority: 1 },
    'Scheduled': { label: 'Scheduled', bg: 'bg-cyan-500', text: 'text-white', priority: 2 },
    'In Progress': { label: 'In Progress', bg: 'bg-amber-500', text: 'text-white', priority: 0 },
    'Work Completed': { label: 'Completed', bg: 'bg-emerald-500', text: 'text-white', priority: 3 },
    'Approved': { label: 'Ready', bg: 'bg-green-500', text: 'text-white', priority: 1 },
    'Quote Submitted': { label: 'Quote Sent', bg: 'bg-purple-500', text: 'text-white', priority: 2 },
    'Awaiting Quote': { label: 'Quote Needed', bg: 'bg-orange-500', text: 'text-white', priority: 0 },
  };
  return statusMap[status] || { label: status || 'Unknown', bg: 'bg-slate-400', text: 'text-white', priority: 99 };
};

const getPriorityConfig = (priority) => {
  const priorityMap = {
    'Emergency': { label: 'Emergency', bg: 'bg-red-600', icon: '!' },
    'Urgent': { label: 'Urgent', bg: 'bg-orange-500', icon: '!!' },
    'High': { label: 'High', bg: 'bg-amber-500', icon: '!' },
    'Standard': { label: 'Standard', bg: 'bg-slate-400', icon: null },
    'Normal': { label: 'Normal', bg: 'bg-slate-400', icon: null },
  };
  return priorityMap[priority] || { label: priority || 'Normal', bg: 'bg-slate-400', icon: null };
};

const getSlaConfig = (slaStatus) => {
  if (slaStatus === 'breach' || slaStatus === 'OVERDUE') {
    return { label: 'OVERDUE', bg: 'bg-red-600', textColor: 'text-white' };
  }
  if (slaStatus === 'warning' || slaStatus === 'DUE_SOON') {
    return { label: 'Due Soon', bg: 'bg-amber-500', textColor: 'text-white' };
  }
  return { label: 'On Track', bg: 'bg-emerald-500', textColor: 'text-white' };
};

// ============================================================
// JOB CARD COMPONENT
// ============================================================
const MobileJobCard = ({ job, onClick }) => {
  const statusConfig = getStatusConfig(job.status);
  const priorityConfig = getPriorityConfig(job.priority);
  const slaConfig = getSlaConfig(job.sla_status);

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-2xl border-2 border-slate-200 p-4 active:scale-[0.98] transition-transform touch-manipulation cursor-pointer"
      data-testid={`job-card-${job.id || job.reference}`}
    >
      {/* Top Row: Reference + Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-slate-800">
            {job.reference || job.job_reference || job.id}
          </span>
          {priorityConfig.icon && (
            <span className={`${priorityConfig.bg} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>
              {priorityConfig.label}
            </span>
          )}
        </div>
        <span className={`${statusConfig.bg} ${statusConfig.text} text-xs font-semibold px-3 py-1 rounded-full`}>
          {statusConfig.label}
        </span>
      </div>

      {/* Site + Address */}
      <div className="mb-3">
        <div className="text-base font-semibold text-slate-900 truncate">
          {job.site_name || job.park_name || 'Site'}
        </div>
        <div className="text-sm text-slate-600 truncate">
          {job.address || job.postcode || 'No address'}
        </div>
      </div>

      {/* Title/Description */}
      <div className="text-sm text-slate-700 line-clamp-2 mb-3">
        {job.title || job.description || 'No description'}
      </div>

      {/* Bottom Row: SLA + Trade */}
      <div className="flex items-center justify-between">
        <span className={`${slaConfig.bg} ${slaConfig.textColor} text-xs font-semibold px-2.5 py-1 rounded-full`}>
          {slaConfig.label}
        </span>
        <span className="text-xs text-slate-500">
          {job.trade || 'General'}
        </span>
      </div>

      {/* Scheduled indicator */}
      {job.scheduled_date && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>
            {new Date(job.scheduled_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
            {job.scheduled_time && ` at ${job.scheduled_time}`}
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function ContractorMobileDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('active'); // active, completed, all
  const [engineerName, setEngineerName] = useState('');
  const [engineerId, setEngineerId] = useState('');

  // Load jobs
  const loadJobs = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        toast.error('Please log in to view jobs');
        navigate('/contractor-company/login');
        return;
      }

      // Try mobile API first
      let res;
      try {
        res = await api.get('/mobile/my-jobs');
        if (res.data.jobs) {
          setJobs(res.data.jobs);
          setEngineerId(res.data.engineer_id);
          return;
        }
      } catch (mobileErr) {
        console.log('Mobile API not available, trying contractor-company API');
      }

      // Fallback to contractor-company API
      res = await api.get('/contractor-company/jobs?limit=100');
      const jobsData = res.data.data || res.data.jobs || [];
      setJobs(jobsData);
      
      // Get engineer name from localStorage
      const name = localStorage.getItem('contractor_portal_name');
      if (name) setEngineerName(name);
      
    } catch (err) {
      console.error('Load error:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired');
        navigate('/contractor-company/login');
      } else {
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadJobs();
    // Auto-refresh every 2 minutes
    const interval = setInterval(() => loadJobs(true), 120000);
    return () => clearInterval(interval);
  }, [loadJobs]);

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    if (filter === 'active') {
      return !['Completed', 'Work Completed', 'Closed', 'Cancelled', 'Paid'].includes(job.status);
    }
    if (filter === 'completed') {
      return ['Completed', 'Work Completed', 'Closed', 'Paid'].includes(job.status);
    }
    return true;
  }).sort((a, b) => {
    // Sort by priority: In Progress first, then by SLA status
    const aStatus = getStatusConfig(a.status).priority;
    const bStatus = getStatusConfig(b.status).priority;
    return aStatus - bStatus;
  });

  // Navigate to job
  const handleJobClick = (job) => {
    const jobId = job.id || job._id || job.job_id;
    const token = getToken();
    if (token) {
      navigate(`/contractor/mobile/job/${jobId}?token=${encodeURIComponent(token)}`);
    } else {
      navigate(`/contractor/mobile/job/${jobId}`);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('contractor_mobile_token');
    localStorage.removeItem('contractor_portal_token');
    localStorage.removeItem('contractor_portal_name');
    localStorage.removeItem('contractor_portal_id');
    navigate('/contractor-company/login');
  };

  // Stats
  const activeCount = jobs.filter(j => !['Completed', 'Work Completed', 'Closed', 'Cancelled', 'Paid'].includes(j.status)).length;
  const urgentCount = jobs.filter(j => ['Emergency', 'Urgent'].includes(j.priority) && !['Completed', 'Work Completed', 'Closed'].includes(j.status)).length;
  const overdueCount = jobs.filter(j => (j.sla_status === 'breach' || j.sla_status === 'OVERDUE') && !['Completed', 'Work Completed', 'Closed'].includes(j.status)).length;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="contractor-mobile-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white sticky top-0 z-50 safe-area-top">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-lg font-bold">My Jobs</div>
              <div className="text-sm text-teal-100 opacity-90">
                {engineerName || 'Field Engineer'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => loadJobs(true)}
                disabled={refreshing}
                className="p-2.5 bg-white/10 rounded-xl active:bg-white/20 transition-colors"
                data-testid="refresh-btn"
              >
                <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button 
                onClick={handleLogout}
                className="p-2.5 bg-white/10 rounded-xl active:bg-white/20 transition-colors"
                data-testid="logout-btn"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex gap-3">
            <div className="flex-1 bg-white/10 rounded-xl px-3 py-2">
              <div className="text-2xl font-bold">{activeCount}</div>
              <div className="text-xs text-teal-100">Active</div>
            </div>
            {urgentCount > 0 && (
              <div className="flex-1 bg-orange-500 rounded-xl px-3 py-2">
                <div className="text-2xl font-bold">{urgentCount}</div>
                <div className="text-xs text-orange-100">Urgent</div>
              </div>
            )}
            {overdueCount > 0 && (
              <div className="flex-1 bg-red-500 rounded-xl px-3 py-2">
                <div className="text-2xl font-bold">{overdueCount}</div>
                <div className="text-xs text-red-100">Overdue</div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Filter Tabs */}
      <div className="sticky top-[120px] z-40 bg-slate-100 px-4 py-3 border-b border-slate-200">
        <div className="flex gap-2">
          {[
            { id: 'active', label: 'Active', count: activeCount },
            { id: 'completed', label: 'Completed', count: jobs.length - activeCount },
            { id: 'all', label: 'All', count: jobs.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex-1 py-2.5 px-3 rounded-xl font-medium text-sm transition-all ${
                filter === tab.id 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Jobs List */}
      <main className="px-4 py-4 pb-24 space-y-3">
        {filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-slate-600 font-medium">No jobs found</p>
            <p className="text-sm text-slate-400 mt-1">
              {filter === 'active' ? 'All jobs completed!' : 'No jobs match this filter'}
            </p>
          </div>
        ) : (
          filteredJobs.map(job => (
            <MobileJobCard 
              key={job.id || job._id || job.job_id} 
              job={job} 
              onClick={() => handleJobClick(job)}
            />
          ))
        )}
      </main>

      {/* Quick Call Button - Fixed Bottom */}
      <div className="fixed bottom-4 right-4 safe-area-bottom">
        <a
          href="tel:01onal234567890"
          className="flex items-center justify-center w-14 h-14 bg-teal-600 text-white rounded-full shadow-lg active:scale-95 transition-transform"
          data-testid="call-helpdesk-btn"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </a>
      </div>
    </div>
  );
}
